import React, { useEffect, useMemo, useRef, useState } from 'react';

function mergeBoard(givens, saved) {
  return (givens || []).map((row, r) => (row || []).map((cell, c) => {
    if (cell != null && cell !== '') return String(cell);
    const v = saved?.[r]?.[c];
    return v == null || v === '' ? '' : String(v);
  }));
}

const MAX_ERRORS = 3;

function normalizeSaved(savedBoard, savedErrors, savedErrorCells) {
  const errs = Number(savedErrors) || 0;
  if (errs >= MAX_ERRORS) {
    return { board: null, errors: 0, errorCells: [] };
  }
  return { board: savedBoard, errors: Math.max(0, errs), errorCells: savedErrorCells || [] };
}

export default function SudokuBoard({
  payload,
  savedBoard,
  savedErrors = 0,
  savedErrorCells,
  onSolved,
  onProgress,
  playStarted = true,
  onGo,
  onDemoClick,
}) {
  const size = payload?.size || payload?.solution?.length || 4;
  const givens = payload?.givens || [];
  const solution = payload?.solution || [];
  const initial = normalizeSaved(savedBoard, savedErrors, savedErrorCells);
  const [grid, setGrid] = useState(() => mergeBoard(givens, initial.board));
  const [selectedDigit, setSelectedDigit] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [errors, setErrors] = useState(initial.errors);
  const [errorCells, setErrorCells] = useState(() => new Set(initial.errorCells));
  const [flash, setFlash] = useState('');
  const solvedRef = React.useRef(false);
  const errorsRef = React.useRef(initial.errors);
  const awaitingGo = !playStarted;

  useEffect(() => {
    const next = normalizeSaved(savedBoard, savedErrors, savedErrorCells);
    setGrid(mergeBoard(givens, next.board));
    setSelectedDigit(null);
    setSelectedCell(null);
    setErrors(next.errors);
    setErrorCells(new Set(next.errorCells));
    errorsRef.current = next.errors;
    solvedRef.current = false;
    if ((Number(savedErrors) || 0) >= MAX_ERRORS) {
      onProgress?.({ board: mergeBoard(givens, null), errors: 0, errorCells: [] });
    }
  }, [payload]);

  useEffect(() => {
    const next = normalizeSaved(savedBoard, savedErrors, savedErrorCells);
    if (next.board?.length) setGrid(mergeBoard(givens, next.board));
  }, [savedBoard]);

  const isGiven = (r, c) => givens?.[r]?.[c] != null;

  const isCorrect = (r, c, board = grid) => {
    const v = board?.[r]?.[c];
    if (v == null || v === '') return false;
    return solution?.[r]?.[c] != null && Number(v) === Number(solution[r][c]);
  };

  const isLocked = (r, c, board = grid) => isGiven(r, c) || isCorrect(r, c, board);

  const isDigitComplete = (digit, board = grid) => {
    if (digit == null || digit === '' || digit === 'erase') return false;
    let placed = 0;
    let needed = 0;
    for (let r = 0; r < size; r += 1) {
      for (let c = 0; c < size; c += 1) {
        if (Number(solution?.[r]?.[c]) === Number(digit)) {
          needed += 1;
          if (Number(board?.[r]?.[c]) === Number(digit)) placed += 1;
        }
      }
    }
    return needed > 0 && placed >= needed;
  };

  const completedDigits = useMemo(() => {
    const done = new Set();
    for (let n = 1; n <= size; n += 1) {
      if (isDigitComplete(String(n))) done.add(String(n));
    }
    return done;
  }, [grid, solution, size]);

  const checkSolved = (next) => {
    if (!solution?.length) return false;
    for (let r = 0; r < size; r += 1) {
      for (let c = 0; c < size; c += 1) {
        if (Number(next[r][c]) !== Number(solution[r][c])) return false;
      }
    }
    return true;
  };

  const persist = (nextGrid, nextErrors, nextErrorCells) => {
    onProgress?.({
      board: nextGrid,
      errors: nextErrors,
      errorCells: Array.from(nextErrorCells),
    });
  };

  const applyCell = (r, c, value) => {
    if (solvedRef.current || isLocked(r, c)) return;
    if (value && isDigitComplete(value)) return;
    if (grid[r]?.[c] === value) return;
    const key = `${r},${c}`;
    const expected = solution?.[r]?.[c];
    const isWrong = value !== '' && expected != null && Number(value) !== Number(expected);
    const next = grid.map((row, i) => (i === r ? row.map((cell, j) => (j === c ? value : cell)) : row));
    let nextErrors = errorsRef.current;
    const nextErrorCells = new Set(errorCells);
    if (!value) {
      nextErrorCells.delete(key);
    } else if (isWrong) {
      nextErrorCells.add(key);
      nextErrors = Math.min(MAX_ERRORS, errorsRef.current + 1);
      errorsRef.current = nextErrors;
      setErrors(nextErrors);
      if (nextErrors < MAX_ERRORS) {
        const remaining = MAX_ERRORS - nextErrors;
        setFlash(`Erreur ! Il reste ${remaining} essai${remaining > 1 ? 's' : ''} autorisé${remaining > 1 ? 's' : ''}.`);
      }
    } else {
      nextErrorCells.delete(key);
    }
    if (isWrong && nextErrors >= MAX_ERRORS) {
      const reset = mergeBoard(givens, null);
      errorsRef.current = 0;
      setErrors(0);
      setErrorCells(new Set());
      setSelectedCell(null);
      setSelectedDigit(null);
      setFlash('3 erreurs : la grille est réinitialisée. Réessayez !');
      setGrid(reset);
      persist(reset, 0, new Set());
      return;
    }
    setGrid(next);
    setErrorCells(nextErrorCells);
    persist(next, nextErrors, nextErrorCells);
    if (value && !isWrong && isDigitComplete(value, next)) {
      setSelectedDigit((cur) => (cur === value ? null : cur));
    }
    if (value && !isWrong && checkSolved(next) && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.();
    }
  };

  const onCellClick = (r, c) => {
    if (awaitingGo) return;
    const value = grid[r]?.[c];
    const given = isGiven(r, c);
    setSelectedCell({ r, c });
    if (value) {
      setSelectedDigit(value);
      return;
    }
    if (given || isLocked(r, c) || solvedRef.current) return;
    if (selectedDigit && selectedDigit !== 'erase' && !isDigitComplete(selectedDigit)) {
      applyCell(r, c, selectedDigit);
    }
  };

  const onDigitClick = (n) => {
    if (awaitingGo) return;
    if (isDigitComplete(n)) return;
    if (selectedDigit === n) {
      setSelectedDigit(null);
      return;
    }
    setSelectedDigit(n);
    if (selectedCell && !isLocked(selectedCell.r, selectedCell.c) && !solvedRef.current) {
      applyCell(selectedCell.r, selectedCell.c, n);
    }
  };

  const onClearDigit = () => {
    if (awaitingGo) return;
    setSelectedDigit(null);
  };

  const onErase = () => {
    if (awaitingGo) return;
    if (selectedCell && !isLocked(selectedCell.r, selectedCell.c) && !solvedRef.current) {
      applyCell(selectedCell.r, selectedCell.c, '');
      return;
    }
    setSelectedDigit((cur) => (cur === 'erase' ? null : 'erase'));
  };

  useEffect(() => {
    const onKey = (e) => {
      if (awaitingGo || !selectedCell || solvedRef.current) return;
      if (isLocked(selectedCell.r, selectedCell.c)) return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        applyCell(selectedCell.r, selectedCell.c, '');
        return;
      }
      if (!/^\d$/.test(e.key)) return;
      const n = Number(e.key);
      if (n < 1 || n > size) return;
      if (isDigitComplete(String(n))) return;
      e.preventDefault();
      setSelectedDigit(String(n));
      applyCell(selectedCell.r, selectedCell.c, String(n));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedCell, size, grid, awaitingGo]);

  const highlightDigit = selectedDigit && selectedDigit !== 'erase' ? selectedDigit : null;
  const gridPaneRef = useRef(null);
  const [paneW, setPaneW] = useState(0);

  useEffect(() => {
    const el = gridPaneRef.current;
    if (!el) return undefined;
    const update = () => setPaneW(el.clientWidth);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [payload, size]);

  const minCell = size > 6 ? 36 : 44;
  const maxCell = size > 6 ? 56 : 64;
  const cellPx = paneW
    ? Math.max(minCell, Math.min(maxCell, Math.floor((paneW * 0.78) / size)))
    : minCell;
  const boxR = size === 9 ? 3 : 2;
  const boxC = size === 6 ? 3 : (size === 9 ? 3 : 2);

  const remaining = MAX_ERRORS - errors;

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <div className="flex items-center justify-center gap-3 min-h-[42px] sm:w-3/4 mx-auto sm:mx-0">
        {onDemoClick ? (
          <button
            type="button"
            onClick={onDemoClick}
            className="inline-flex items-center gap-2 rounded-md border border-orange-300/80 dark:border-orange-800/70 bg-orange-50/90 dark:bg-orange-950/40 px-4 py-2.5 text-sm font-semibold text-[#f76f4b] hover:bg-orange-100 dark:hover:bg-orange-950/70 shadow-sm"
          >
            Démo
          </button>
        ) : null}
        {awaitingGo ? (
          <button
            type="button"
            onClick={() => onGo?.()}
            className="inline-flex items-center justify-center rounded-md bg-[#f76f4b] hover:bg-[#e85f3a] text-white text-sm font-bold tracking-wide px-4 py-2.5 shadow-sm min-w-[5.5rem]"
          >
            Go
          </button>
        ) : null}
      </div>
      {!awaitingGo ? (
        <div className={`text-xs font-semibold sm:w-3/4 text-center ${errors > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          Essais restants : {remaining} / {MAX_ERRORS}
        </div>
      ) : null}
      {flash && !awaitingGo ? (
        <p className="text-xs font-medium text-red-600 dark:text-red-400 text-center sm:w-3/4">{flash}</p>
      ) : null}
      <div className="flex w-full min-w-0 flex-col items-stretch gap-4 sm:flex-row sm:items-start">
        <div ref={gridPaneRef} className="flex w-full min-w-0 justify-center sm:w-3/4">
          <div
            className={`inline-grid rounded-sm overflow-hidden border-2 border-gray-800 dark:border-gray-200 ${
              awaitingGo ? 'blur-[8px] opacity-75 pointer-events-none' : ''
            }`}
            style={{ gridTemplateColumns: `repeat(${size}, ${cellPx}px)` }}
          >
            {grid.map((row, r) =>
              row.map((cell, c) => {
                const boxRight = (c + 1) % boxC === 0 && c < size - 1;
                const boxBottom = (r + 1) % boxR === 0 && r < size - 1;
                const locked = isLocked(r, c);
                const match = !!(highlightDigit && cell === highlightDigit);
                const selected = selectedCell?.r === r && selectedCell?.c === c;
                const wrong = errorCells.has(`${r},${c}`);
                return (
                  <button
                    key={`${r}-${c}`}
                    type="button"
                    disabled={awaitingGo}
                    onClick={() => onCellClick(r, c)}
                    className={`text-center font-semibold relative ${
                      wrong
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-800 dark:text-gray-100'
                    } ${
                      match
                        ? locked
                          ? 'bg-orange-200 dark:bg-orange-900/60'
                          : 'bg-orange-100 dark:bg-orange-950/50'
                        : locked
                          ? 'bg-gray-100 dark:bg-gray-700'
                          : 'bg-white dark:bg-gray-800'
                    } ${selected ? 'z-[1]' : ''} ${boxRight ? 'border-r-2 border-r-gray-800 dark:border-r-gray-200' : 'border-r border-r-gray-300 dark:border-r-gray-600'} ${boxBottom ? 'border-b-2 border-b-gray-800 dark:border-b-gray-200' : 'border-b border-b-gray-300 dark:border-b-gray-600'}`}
                    style={{
                      width: cellPx,
                      height: cellPx,
                      fontSize: size > 6 ? 16 : 18,
                      boxShadow: selected ? 'inset 0 0 0 3px #f76f4b' : undefined,
                    }}
                  >
                    {cell}
                  </button>
                );
              })
            )}
          </div>
        </div>
        <div className={`flex w-full min-w-0 flex-col items-center gap-1.5 sm:w-1/4 ${awaitingGo ? 'blur-[8px] opacity-75 pointer-events-none' : ''}`}>
          {Array.from({ length: size }, (_, i) => String(i + 1)).map((n) => {
            const done = completedDigits.has(n);
            return (
              <button
                key={n}
                type="button"
                disabled={awaitingGo || done}
                tabIndex={done || awaitingGo ? -1 : 0}
                aria-hidden={done || undefined}
                onClick={() => onDigitClick(n)}
                className={`h-9 w-9 rounded-md text-sm font-bold border ${
                  done
                    ? 'invisible pointer-events-none'
                    : selectedDigit === n
                      ? 'bg-[#f76f4b] text-white border-[#f76f4b]'
                      : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                }`}
              >
                {n}
              </button>
            );
          })}
          <button
            type="button"
            disabled={awaitingGo}
            onClick={onErase}
            className={`mt-1 h-9 px-2 rounded-md text-xs font-semibold border ${
              selectedDigit === 'erase'
                ? 'bg-[#f76f4b] text-white border-[#f76f4b]'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'
            }`}
          >
            Effacer
          </button>
          <button
            type="button"
            disabled={awaitingGo}
            onClick={onClearDigit}
            className="h-9 px-2 rounded-md text-xs font-semibold border bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600"
          >
            Désélectionner
          </button>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 text-center sm:w-3/4">
        {awaitingGo
          ? 'Appuyez sur Go pour démarrer le chrono, ou Démo pour voir le principe.'
          : 'Touchez une case, puis un chiffre, Effacer, ou le clavier.'}
      </p>
    </div>
  );
}
